

import React, { useState, useCallback, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    SelectChangeEvent,
    MenuItem,
    TextField,
    Grid,
    Box,
    IconButton,
    InputAdornment,
    Typography,
    Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

export const AddContainer = ({ openAddContainers, handleAddContainersClose, handleAddContainer, loadContainers, addContainerType, parentData, isSaving = false }: any) => {
    const [selectedContainer, setSelectedContainer] = useState('');
    const [itemUnitTypes, setItemUnitTypes] = useState<Record<number, string>>({});
    const [isUnitTypeChanged, setIsUnitTypeChanged] = useState<Record<number, boolean>>({});
    const [multiplier, setMultiplier] = useState(1);
    const [unit, setUnit] = useState<number | ''>(1);
    const [rowData, setRowData] = useState<any>();
    const [selectedBottleGroup, setSelectedBottleGroup] = useState('');
    const isGroup = addContainerType === 'group';
    const selectedGroup = parentData?.bottleGroups?.find((group: any) => group?.id === selectedBottleGroup);
    const requestedUnits = isGroup ? selectedGroup?.requestedQuantity ?? '' : unit;

    // Fields stay disabled until a container is picked from the dropdown
    const unitType = isGroup ? 'Individual' : itemUnitTypes[selectedContainer] ?? 'Individual';
    const isDisabled = !selectedContainer || unitType?.toLowerCase() !== 'individual';
    const isSubmitDisabled = isSaving || !selectedContainer || (isGroup && (!selectedGroup || requestedUnits === ''));

    const resetForm = () => {
        setSelectedContainer('');
        setItemUnitTypes({});
        setIsUnitTypeChanged({});
        setSelectedBottleGroup('');
        setMultiplier(1);
        setUnit(1);
        // Reset addContainerType if needed
        // setAddContainerType(''); // Uncomment if you want to reset this as well
    };

    useEffect(() => {
        setRowData(parentData);
    }, [parentData]);

    const handleContainerChange = (event: SelectChangeEvent<string>) => {
        setSelectedContainer(event.target.value);
        setMultiplier(1);
        setUnit(1);
    };

    const handleUnitBlur = (value: string) => {
        const parsed = value === '' ? 1 : Number(value);
        setUnit(isNaN(parsed) ? 1 : parsed);
    };

    const handleClose = () => {
        if (isSaving) return;
        resetForm();
        handleAddContainersClose();
    };

    const handleSubmit = async () => {
        if (isSubmitDisabled) return;

        let payload:any = {};
        if(addContainerType === 'container' || addContainerType === 'group'){
           payload = {
            containerID: selectedContainer,
            requestedQuantity: requestedUnits === '' ? 0 : requestedUnits,
            comments: '',
            multiplier,
            orderId: parentData?.id,
            bulkType: unitType,
            isBulkOrder: unitType?.toLowerCase() !== 'individual',
            ...(addContainerType === 'group' && { groupId: selectedBottleGroup }),
        };
        } else{
            payload = {
                PackageId: selectedContainer,
                requestedQuantity: unit === '' ? 0 : unit,
                comments: '',
                orderId: parentData?.id
            };
        }

        const success = await handleAddContainer(payload);
        if (success !== false) resetForm();
    };

    const getBulkUnitType = (item: any) => `${item?.bulkType ?? ''}`.trim();

    const getUnitTypeOptions = (item: any) => {
        if (isGroup) return ['Individual'];
        const bulkUnitType = getBulkUnitType(item);
        return bulkUnitType && bulkUnitType !== 'Individual'
            ? ['Individual', bulkUnitType]
            : ['Individual'];
    };

    const renderUnitTypeField = (item: any) => (
        <TextField
            select
            label="Unit Type"
            size="small"
            fullWidth
            value={unitType}
            disabled={isGroup || isSaving}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => handleItemUnitTypeChange(item, event.target.value)}
        >
            {getUnitTypeOptions(item).map((option) => (
                <MenuItem key={option} value={option}>
                    {option}
                </MenuItem>
            ))}
        </TextField>
    );

    const handleItemUnitTypeChange = useCallback((item: any, value: string) => {
        setItemUnitTypes((prev) => ({
            ...prev,
            [item?.id]: value
        }));

        setIsUnitTypeChanged((prev) => ({
            ...prev,
            [item?.id]: value !== "Individual"
        }));
    }, []);

    const selectedItem = loadContainers?.find(
        (item: any) => item?.id === selectedContainer
    );

    const isContainer = (addContainerType === 'container') || (addContainerType === 'group');

    return (
        <div>
            <Dialog
                open={openAddContainers}
                onClose={handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {isContainer ? 'Add Container' : 'Add Package'}
                </DialogTitle>

                <DialogContent>
                    {addContainerType === 'group' && (
                        <FormControl required fullWidth size="small" sx={{ mt: 1 }}>
                            <InputLabel id="bottle-group-label" sx={{ fontSize: '12px' }}>
                                Bottle Group
                            </InputLabel>

                            <Select
                                labelId="bottle-group-label"
                                id="bottle-group"
                                value={selectedBottleGroup}
                                label="Bottle Group"
                                sx={{ fontSize: '12px' }}
                                onChange={(event) =>
                                    setSelectedBottleGroup(event.target.value)
                                }
                            >
                                {rowData?.bottleGroups?.map((item: any) => (
                                    <MenuItem
                                        key={item?.id}
                                        value={item?.id}
                                        sx={{ fontSize: '12px' }}
                                    >
                                        {item?.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <Autocomplete
                            size="small"
                            options={loadContainers || []}

                            value={
                                loadContainers?.find(
                                    (item: any) => item?.id === selectedContainer
                                ) || null
                            }

                            onChange={(event, newValue: any) => {
                                handleContainerChange({
                                    target: {
                                        value: newValue?.id || '',
                                    },
                                } as any);
                            }}

                            getOptionLabel={(item: any) => {
                                if (!item) return '';

                                // Container
                                if (item?.bulkQuantity !== undefined) {
                                    return [
                                        item?.name,
                                        item?.volume
                                            ? `${item.volume} ${item.volumeUnit || ''}`
                                            : '',
                                        item?.color || '',
                                    ]
                                        .filter(Boolean)
                                        .join(' - ');
                                }

                                // Quality
                                return [
                                    item?.name,
                                    item?.size || '',
                                ]
                                    .filter(Boolean)
                                    .join(' - ');
                            }}

                            filterOptions={(options, state) => {
                                const searchText = state.inputValue
                                    .toLowerCase()
                                    .trim();

                                return options.filter((item: any) => {
                                    const searchableText = [
                                        item?.name,
                                        item?.volume,
                                        item?.volumeUnit,
                                        item?.color,
                                        item?.size,
                                        item?.comments,
                                        item?.group,
                                        item?.code,
                                        item?.description,
                                    ]
                                        .filter(
                                            (value) =>
                                                value !== undefined &&
                                                value !== null
                                        )
                                        .join(' ')
                                        .toLowerCase();

                                    return searchableText.includes(searchText);
                                });
                            }}

                            isOptionEqualToValue={(option: any, value: any) =>
                                option?.id === value?.id
                            }

                            renderOption={(props, item: any) => (
                                <li {...props} key={item.id}>
                                    {item?.bulkQuantity !== undefined ? (
                                        <>
                                            {item?.name}

                                            {item?.volume &&
                                                ` - ${item.volume} ${item.volumeUnit || ''}`}

                                            {item?.color &&
                                                ` - ${item.color}`}
                                        </>
                                    ) : (
                                        <>
                                            {item?.name}

                                            {item?.size &&
                                                ` - ${item.size}`}
                                        </>
                                    )}
                                </li>
                            )}

                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={
                                        isContainer
                                            ? 'Container'
                                            : 'Quality'
                                    }
                                    placeholder={
                                        isContainer
                                            ? 'Search Container'
                                            : 'Search Quality'
                                    }
                                />
                            )}
                        />
                    </FormControl>

                    {selectedItem && (
                        <Grid
                            container
                            spacing={1}
                            sx={{
                                mt: 2,
                                mb: 1.5,
                            }}
                            onClick={(event) =>
                                event.stopPropagation()
                            }
                        >
                            {/* Unit Type */}
                            {addContainerType !== 'quality' && !isGroup && (
                                <Grid
                                    item
                                    xs={12}
                                    sm={isContainer ? 4 : 6}
                                    md={isContainer ? 4 : 6}
                                    lg={isContainer ? 4 : 6}
                                >
                                    {renderUnitTypeField(selectedItem)}
                                </Grid>
                            )}

                            {/* Multiplier - Container ONLY */}
                            {isContainer && (
                                <Grid
                                    item
                                    xs={12}
                                    sm={4}
                                    md={4}
                                    lg={4}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            flexWrap: 'nowrap',
                                            width: '100%',
                                        }}
                                    >
                                        {isDisabled ? (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    whiteSpace: 'nowrap',
                                                    flexShrink: 0,
                                                    pt: 1,
                                                }}
                                            >
                                                Quantity:{' '}
                                                <span
                                                    style={{
                                                        fontWeight: 'bold',
                                                        backgroundColor:
                                                            '#e0e0e0',
                                                        borderRadius: '4px',
                                                        padding: '8px',
                                                    }}
                                                >
                                                    {
                                                        selectedItem
                                                            ?.bulkQuantity
                                                    }
                                                </span>
                                            </Typography>
                                        ) : (
                                            <TextField
                                                label="Multiplier"
                                                size="small"
                                                type="number"
                                                value={multiplier}
                                                disabled={isDisabled}
                                                onClick={(event) =>
                                                    event.stopPropagation()
                                                }
                                                onChange={(event) => {
                                                    const value =
                                                        event.target.value;

                                                    // Allow empty while typing
                                                    if (value === '') {
                                                        setMultiplier(1);
                                                        return;
                                                    }

                                                    const val =
                                                        parseInt(
                                                            value,
                                                            10
                                                        );

                                                    if (
                                                        !isNaN(val) &&
                                                        val >= 1 &&
                                                        val <= 6
                                                    ) {
                                                        setMultiplier(
                                                            val
                                                        );
                                                    }
                                                }}
                                                inputProps={{
                                                    min: 1,
                                                    max: 6,
                                                    style: {
                                                        textAlign: 'center',
                                                        padding: '6px 0',
                                                    },
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <IconButton
                                                                size="small"
                                                                disabled={
                                                                    isDisabled
                                                                }
                                                                onClick={(
                                                                    event
                                                                ) => {
                                                                    event.stopPropagation();

                                                                    setMultiplier(
                                                                        (
                                                                            prev: number
                                                                        ) =>
                                                                            Math.max(
                                                                                1,
                                                                                Number(
                                                                                    prev ||
                                                                                    1
                                                                                ) -
                                                                                1
                                                                            )
                                                                    );
                                                                }}
                                                                sx={{
                                                                    width: 28,
                                                                    height: 28,
                                                                    p: 0,
                                                                    borderRadius:
                                                                        '4px',
                                                                    color: '#555',
                                                                    transition:
                                                                        'all 0.2s ease',

                                                                    '&:hover':
                                                                    {
                                                                        backgroundColor:
                                                                            '#e3f2fd',
                                                                        color: '#1976d2',
                                                                    },

                                                                    '&:active':
                                                                    {
                                                                        backgroundColor:
                                                                            '#bbdefb',
                                                                        transform:
                                                                            'scale(0.9)',
                                                                    },
                                                                }}
                                                            >
                                                                <RemoveIcon
                                                                    sx={{
                                                                        fontSize: 14,
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),

                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                size="small"
                                                                disabled={
                                                                    isDisabled
                                                                }
                                                                onClick={(
                                                                    event
                                                                ) => {
                                                                    event.stopPropagation();

                                                                    setMultiplier(
                                                                        (
                                                                            prev: number
                                                                        ) =>
                                                                            Math.min(
                                                                                6,
                                                                                Number(
                                                                                    prev ||
                                                                                    1
                                                                                ) +
                                                                                1
                                                                            )
                                                                    );
                                                                }}
                                                                sx={{
                                                                    width: 28,
                                                                    height: 28,
                                                                    p: 0,
                                                                    borderRadius:
                                                                        '4px',
                                                                    color: '#555',
                                                                    transition:
                                                                        'all 0.2s ease',

                                                                    '&:hover':
                                                                    {
                                                                        backgroundColor:
                                                                            '#e8f5e9',
                                                                        color: '#2e7d32',
                                                                    },

                                                                    '&:active':
                                                                    {
                                                                        backgroundColor:
                                                                            '#c8e6c9',
                                                                        transform:
                                                                            'scale(0.9)',
                                                                    },
                                                                }}
                                                            >
                                                                <AddIcon
                                                                    sx={{
                                                                        fontSize: 14,
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                sx={{
                                                    width: '100%',

                                                    '& .MuiInputBase-root':
                                                    {
                                                        height: 35,
                                                    },

                                                    '& .MuiInputLabel-root':
                                                    {
                                                        fontSize: 12,
                                                    },

                                                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                                                    {
                                                        WebkitAppearance:
                                                            'none',
                                                        margin: 0,
                                                    },

                                                    '& input[type=number]':
                                                    {
                                                        MozAppearance:
                                                            'textfield',
                                                    },
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Grid>
                            )}

                            {/* Units - Both Container & Quality */}
                            {!isGroup && (
                            <Grid
                                item
                                xs={12}
                                sm={isContainer ? 4 : 6}
                                md={isContainer ? 4 : 6}
                                lg={isContainer ? 4 : 6}
                            >
                                <TextField
                                    fullWidth
                                    label="Units"
                                    type="number"
                                    size="small"
                                    value={requestedUnits}
                                    InputProps={{ readOnly: isGroup }}
                                    disabled={isSaving || (isGroup && !selectedGroup)}
                                    onClick={(event) =>
                                        event.stopPropagation()
                                    }
                                    onChange={(event) => {
                                        if (isGroup) return;
                                        const value =
                                            event.target.value;

                                        setUnit(
                                            value === ''
                                                ? ''
                                                : Number(value)
                                        );
                                    }}
                                    onBlur={(event) =>
                                        !isGroup && handleUnitBlur(
                                            event.target.value
                                        )
                                    }
                                    inputProps={{
                                        min: 1,
                                    }}
                                />
                            </Grid>
                            )}
                        </Grid>
                    )}

                </DialogContent>

                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};